/**
 * Geometry utility functions for polygon operations
 */

export interface Point {
  x: number;
  y: number;
}

/**
 * Calculate the centroid (geometric center) of a polygon
 */
export function calculatePolygonCentroid(polygon: Point[]): Point {
  if (polygon.length === 0) return { x: 0, y: 0 };
  if (polygon.length === 1) return { ...polygon[0] };

  let area = 0;
  let cx = 0;
  let cy = 0;

  for (let i = 0; i < polygon.length; i++) {
    const j = (i + 1) % polygon.length;
    const xi = polygon[i].x;
    const yi = polygon[i].y;
    const xj = polygon[j].x;
    const yj = polygon[j].y;

    const cross = xi * yj - xj * yi;
    area += cross;
    cx += (xi + xj) * cross;
    cy += (yi + yj) * cross;
  }

  area = area / 2;

  // Handle degenerate polygons (lines)
  if (Math.abs(area) < 0.0001) {
    // Fall back to simple average
    const sumX = polygon.reduce((sum, p) => sum + p.x, 0);
    const sumY = polygon.reduce((sum, p) => sum + p.y, 0);
    return {
      x: sumX / polygon.length,
      y: sumY / polygon.length
    };
  }

  cx = cx / (6 * area);
  cy = cy / (6 * area);

  return { x: cx, y: cy };
}

/**
 * Check if a point is inside a polygon using ray casting algorithm
 */
export function isPointInPolygon(point: Point, polygon: Point[]): boolean {
  if (polygon.length < 3) return false;

  let inside = false;
  const x = point.x;
  const y = point.y;

  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].x;
    const yi = polygon[i].y;
    const xj = polygon[j].x;
    const yj = polygon[j].y;

    const intersect = ((yi > y) !== (yj > y)) &&
      (x < (xj - xi) * (y - yi) / (yj - yi) + xi);

    if (intersect) inside = !inside;
  }

  return inside;
}

/**
 * Draw text with a background rectangle
 */
export function drawTextWithBackground(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  options: {
    textColor: string;
    font: string;
    backgroundColor: string;
    padding?: number;
    textAlign?: CanvasTextAlign;
    textBaseline?: CanvasTextBaseline;
  }
): void {
  const {
    font,
    textColor,
    backgroundColor,
    padding = 4,
    textAlign = 'center',
    textBaseline = 'middle'
  } = options;

  ctx.font = font;
  ctx.textAlign = textAlign;
  ctx.textBaseline = textBaseline;

  // Measure text
  const metrics = ctx.measureText(text);
  const textWidth = metrics.width;
  const textHeight = metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent;

  // Calculate background position based on alignment
  let bgX = x;
  let bgY = y;

  if (textAlign === 'center') {
    bgX = x - textWidth / 2 - padding;
  } else if (textAlign === 'right') {
    bgX = x - textWidth - padding;
  } else {
    bgX = x - padding;
  }

  if (textBaseline === 'middle') {
    bgY = y - textHeight / 2 - padding;
  } else if (textBaseline === 'top') {
    bgY = y - padding;
  } else if (textBaseline === 'bottom') {
    bgY = y - textHeight - padding;
  } else {
    bgY = y - textHeight / 2 - padding;
  }

  // Draw background
  ctx.fillStyle = backgroundColor;
  ctx.fillRect(bgX, bgY, textWidth + padding * 2, textHeight + padding * 2);

  // Draw text
  ctx.fillStyle = textColor;
  ctx.fillText(text, x, y);
}

/**
 * Draw text with an outline/stroke
 */
export function drawTextWithOutline(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  options: {
    textColor: string;
    font: string;
    strokeColor: string;
    strokeWidth: number;
    textAlign?: CanvasTextAlign;
    textBaseline?: CanvasTextBaseline;
  }
): void {
  const {
    font,
    textColor,
    strokeColor,
    strokeWidth,
    textAlign = 'center',
    textBaseline = 'middle'
  } = options;

  ctx.font = font;
  ctx.textAlign = textAlign;
  ctx.textBaseline = textBaseline;

  // Draw stroke
  ctx.strokeStyle = strokeColor;
  ctx.lineWidth = strokeWidth;
  ctx.strokeText(text, x, y);

  // Draw fill
  ctx.fillStyle = textColor;
  ctx.fillText(text, x, y);
}

/**
 * Convert a line defined by two points into a rectangle polygon with given width
 */
export const lineToRectanglePolygon = (
  start: { x: number; y: number },
  end: { x: number; y: number },
  width: number = 5
): { x: number; y: number }[] => {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const length = Math.sqrt(dx * dx + dy * dy);
  
  if (length === 0) return [];
  
  // normalized and scaled by width/2
  const perpX = -dy / length * width / 2;
  const perpY = dx / length * width / 2;
  
  // Create rectangle with 4 corners
  return [
    { x: start.x + perpX, y: start.y + perpY },
    { x: end.x + perpX, y: end.y + perpY },
    { x: end.x - perpX, y: end.y - perpY },
    { x: start.x - perpX, y: start.y - perpY }
  ];
};
